export type AlertVideoListItem = {
  _id: string;
  url?: string | null;
  status?: string;
  cameraId: number;
  createdAt?: string;
  updatedAt?: string;
  hasAudio?: boolean;
  uploadPercentage?: number | null;
};

const IN_FLIGHT_STATUSES = new Set(['pending', 'startedProcessing']);

const TERMINAL_STATUSES = new Set([
  'completed',
  'failedToRequest',
  'failedToUpload',
  'failedFinally',
  'failedAttemptsLimitExceeded',
  'failedDeviceRemovedFromFleet',
]);

/** Higher rank wins when multiple videos exist for the same camera. */
const STATUS_RANK: Record<string, number> = {
  completed: 50,
  failedToRequest: 40,
  failedToUpload: 40,
  failedFinally: 40,
  failedAttemptsLimitExceeded: 40,
  failedDeviceRemovedFromFleet: 40,
  startedProcessing: 20,
  pending: 10,
};

function videoRank(video: AlertVideoListItem): number {
  let rank = STATUS_RANK[video.status ?? ''] ?? 0;
  if (video.url) rank += 100;
  return rank;
}

function isNewer(candidate: AlertVideoListItem, current: AlertVideoListItem): boolean {
  const candidateTs = Date.parse(candidate.updatedAt ?? candidate.createdAt ?? '');
  const currentTs = Date.parse(current.updatedAt ?? current.createdAt ?? '');
  if (!Number.isNaN(candidateTs) && !Number.isNaN(currentTs) && candidateTs !== currentTs) {
    return candidateTs > currentTs;
  }
  return Date.parse(candidate.createdAt ?? '') > Date.parse(current.createdAt ?? '');
}

export function isTerminalVideoStatus(status?: string): boolean {
  return TERMINAL_STATUSES.has(status ?? '');
}

export function pickBestVideoForCamera(
  current: AlertVideoListItem | undefined,
  candidate: AlertVideoListItem,
): AlertVideoListItem {
  if (!current) return candidate;

  const currentRank = videoRank(current);
  const candidateRank = videoRank(candidate);

  if (candidateRank > currentRank) return candidate;
  if (candidateRank < currentRank) return current;
  return isNewer(candidate, current) ? candidate : current;
}

export function groupVideosByCamera(
  videos: AlertVideoListItem[] | undefined,
): Record<number, AlertVideoListItem> {
  if (!videos?.length) return {};

  return videos.reduce((acc, curr) => {
    const existing = acc[curr.cameraId];
    acc[curr.cameraId] = pickBestVideoForCamera(existing, curr);
    return acc;
  }, {} as Record<number, AlertVideoListItem>);
}

/** Camera IDs the UI always tracks (outward=1, inward=2), regardless of which tab is active. */
export const TRACKED_CAMERA_IDS = [1, 2];

export function shouldPollAlertVideos(videos: AlertVideoListItem[] | undefined): boolean {
  if (!videos?.length) return true;
  if (videos.some((video) => IN_FLIGHT_STATUSES.has(video.status ?? ''))) return true;

  // Keep polling until every tracked camera has a terminal-status video.
  // A camera the user hasn't requested yet (or whose record hasn't arrived)
  // has no entry at all — that must not be mistaken for "done", otherwise
  // the single shared interval stops before the other tab's video exists.
  const byCameraId = groupVideosByCamera(videos);
  return TRACKED_CAMERA_IDS.some((cameraId) => {
    const video = byCameraId[cameraId];
    return !video || !isTerminalVideoStatus(video.status);
  });
}

export function findNewlyCompletedVideoIds(
  videos: AlertVideoListItem[] | undefined,
  previousStatuses: Record<string, string>,
): string[] {
  if (!videos?.length) return [];

  return videos
    .filter((video) => video.status === 'completed' && previousStatuses[video._id] !== 'completed')
    .map((video) => video._id);
}

export function isUploadingVideo(video: AlertVideoListItem | undefined): boolean {
  if (!video || isTerminalVideoStatus(video.status)) return false;
  return video.status === 'startedProcessing'
    || (typeof video.uploadPercentage === 'number' && video.uploadPercentage >= 0);
}

/** Once a camera enters upload, do not regress to "request pending" on transient polls. */
export function nextUploadPhaseByCamera(
  videos: Record<number, AlertVideoListItem> | undefined,
  previous: Record<number, true>,
): Record<number, true> {
  if (!videos) return previous;

  const next: Record<number, true> = { ...previous };
  Object.entries(videos).forEach(([cameraId, video]) => {
    const camId = Number(cameraId);
    if (video.url || isTerminalVideoStatus(video.status)) {
      delete next[camId];
      return;
    }
    if (isUploadingVideo(video)) {
      next[camId] = true;
    }
  });
  return next;
}

export function shouldShowAlertVideoUploadUi(
  video: AlertVideoListItem | undefined,
  uploadPhaseLocked: boolean,
  stableUploadProgress: number | null,
): boolean {
  if (video?.url) return false;
  return isUploadingVideo(video)
    || stableUploadProgress !== null
    || (video?.status === 'pending' && uploadPhaseLocked);
}

export function shouldShowAlertVideoPendingUi(
  video: AlertVideoListItem | undefined,
  uploadPhaseLocked: boolean,
  stableUploadProgress: number | null,
): boolean {
  if (video?.url) return false;
  return video?.status === 'pending'
    && !shouldShowAlertVideoUploadUi(video, uploadPhaseLocked, stableUploadProgress);
}
